import { pluralize } from './common';

/**
 * ALL copy for the plan requirements modal and blocked-plan card states.
 *
 * Single source of truth, deliberately isolated from logic so wording
 * iterates without touching behavior: every string is a plain template
 * function returning text with `**bold**` markers (rendered as <strong> by
 * the modal). Edit here, nothing else.
 *
 * Voice rules (see project spec): positive framing (lead with what the plan
 * includes), active voice, contractions, no "not available"/"can't", no
 * banned words (just/simple/easy/please/sorry), no em dashes, bold the
 * destination including its arrow, numbers pre-formatted with commas.
 */

/** Card / chrome */
export const CARD_COPY = {
    viewRequirements: 'View requirements',
    currentPlan: 'Current plan',
    recommendedBadge: 'Recommended for your audience',
};

/** Over-the-top audience handoff shown on non-entry plan cards. */
export const CONTACT_TEAM_COPY = {
    action: 'Contact us',
    detail: 'to talk to our team',
    href: 'mailto:concierge@ghost.org',
};

/** Standard requirements modal chrome */
export const STANDARD_MODAL_COPY = {
    title: ({ targetPlan }) => `Before you switch to ${targetPlan}`,
    // lead is empty on trial; "You're on {currentPlan}. " on a paid plan
    sub: ({ targetPlan, currentPlan }) =>
        `${currentPlan ? `You're on ${currentPlan}. ` : ''}To switch to ${targetPlan}, here's what to check:`,
};

/** Focused audience modal (member limits block the plan) */
export const AUDIENCE_MODAL_COPY = {
    title: ({ targetPlan }) => `${targetPlan} fits a smaller audience`,
    // The blocked plan is the one the user is currently on: acknowledge it
    // rather than framing their own plan as one they're shopping for
    titleCurrentPlan: ({ targetPlan }) => `You've outgrown ${targetPlan}`,
    sub: ({ targetPlan, memberCount, memberLimit }) =>
        `You have **${memberCount}** members, and ${targetPlan} supports **up to ${memberLimit}**.`,
    note: ({ recommendedPlan }) => `**${recommendedPlan}** is the right plan for your audience.`,
    // No plan fits the audience: never name a plan that does not exist
    noteNoFit: () =>
        `Contact us at **concierge@ghost.org** to find the right fit for your audience.`,
    // The recommendation is the plan the user is already on (downgrade blocked)
    noteCurrentPlan: ({ recommendedPlan }) =>
        `You're already on **${recommendedPlan}**, the right plan for your audience.`,
};

/**
 * One entry per blocker `limit` key (see PlanBlocker in ProductUtils).
 * Each returns {title, body}; bodies bold their destination.
 */
export const REQUIREMENT_COPY = {
    customThemes: {
        title: () => 'Theme',
        body: ({ targetPlan, requiredTheme, currentTheme, officialOnly }) => {
            if (officialOnly) {
                return `${targetPlan} uses official Ghost themes. Switch your active theme to an official one in **Settings → Theme**.`;
            }
            if (!currentTheme) {
                return `${targetPlan} uses the official ${requiredTheme} theme. Switch your active theme to ${requiredTheme} in **Settings → Theme**.`;
            }
            return `${targetPlan} uses the official ${requiredTheme} theme. Switch your active theme from ${currentTheme} to ${requiredTheme} in **Settings → Theme**.`;
        },
    },
    limitAnalytics: {
        title: () => 'Web Analytics',
        body: ({ higherTier }) =>
            `Web Analytics comes with ${higherTier} and up. Turn it off in **Settings → Analytics**.`,
    },
    staff: {
        title: () => 'Staff users',
        body: ({ targetPlan, staffLimit, staffLimitRaw, staffCount, staffOver, staffOverRaw }) =>
            `${targetPlan} includes ${staffLimit} staff ${pluralize(staffLimitRaw, 'user', 'users')}. You have ${staffCount}. Remove ${staffOver} staff ${pluralize(staffOverRaw, 'user', 'users')} or change their ${pluralize(staffOverRaw, 'role', 'roles')} to 'Contributor' in **Settings → Staff**.`,
    },
    newsletters: {
        title: () => 'Newsletters',
        body: ({
            targetPlan,
            newsletterLimit,
            newsletterLimitRaw,
            newsletterCount,
            newsletterOver,
            newsletterOverRaw,
        }) => {
            if (newsletterLimitRaw === 1) {
                return `${targetPlan} includes 1 newsletter. You have ${newsletterCount}. Keep one and archive the rest in **Settings → Newsletters**.`;
            }
            return `${targetPlan} includes ${newsletterLimit} newsletters. You have ${newsletterCount}. Archive ${newsletterOver} ${pluralize(newsletterOverRaw, 'newsletter', 'newsletters')} in **Settings → Newsletters**.`;
        },
    },
    limitAutomations: {
        title: () => 'Automations',
        body: ({
            targetPlan,
            automationLimit,
            automationLimitRaw,
            automationCount,
            automationOver,
            automationOverRaw,
        }) => {
            if (automationLimitRaw === 1) {
                return `${targetPlan} includes 1 active automation. You have ${automationCount}. Keep one and remove the rest in **Automations**.`;
            }
            return `${targetPlan} includes ${automationLimit} active automations. You have ${automationCount}. Remove ${automationOver} ${pluralize(automationOverRaw, 'automation', 'automations')} in **Automations**.`;
        },
    },
    limitStripeConnect: {
        title: () => 'Payments',
        body: ({ higherTier }) =>
            `Paid subscriptions come with ${higherTier} and up. Disconnecting Stripe turns off paid memberships, so move active members first, then disconnect in **Settings → Tiers**.`,
    },
    customSendingDomain: {
        title: () => 'Sending domain',
        body: ({ higherTier }) =>
            `Your newsletter sends from a custom domain, which ${higherTier} and up support. Remove it in **Domain → Sending domain** before switching.`,
    },
    customIntegrations: {
        title: () => 'Custom integrations',
        body: ({ targetPlan }) =>
            `${targetPlan} includes built-in integrations. Remove your custom integrations in **Settings → Integrations**.`,
    },
    subdirectory: {
        title: () => 'Subdirectory',
        body: ({ targetPlan }) =>
            `Your site runs in a subdirectory setup. Reach out to **support@ghost.org** to remove it before switching to ${targetPlan}.`,
    },
    ghostVersion: {
        title: () => 'Ghost version',
        body: ({ targetPlan }) =>
            `${targetPlan} runs on Ghost 4.0 and up. Contact **support@ghost.org** to update your site before switching.`,
    },
};
